import { ArrowBack } from '@mui/icons-material';
import CircleIcon from '@mui/icons-material/Circle';
import { Timeline, TimelineItem } from '@mui/lab';
import { Grid, Box, Stack, Avatar, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useState, useEffect, useCallback } from 'react';
import {
  BiDetail,
  BiEnvelope,
  BiHistory,
  BiMessageDetail,
  BiPhone,
  BiTimeFive,
} from 'react-icons/bi';
import { FaFileAlt } from 'react-icons/fa';
import { createSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getClosureById,
  getClosureByCandidiateId,
  getJobCandidateInteraction,
  getPlacementStatusList,
  getClosureDetailsById,
  ActivityLogDTO,
  getActivityLogs,
  getActivityLogsByEntity,
  getContractPlacementById,
} from 'wisestep-api';
import {
  Button,
  ScrollableTabs,
  TabTextVariant,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsSelectObj,
  WsChip,
  WsChipVariants,
  WsTimeLine,
  formatDateInDayMonthYearTime,
} from 'wisestep-base';

import CommunicationBar from '../../components/communicationBar/CommunicationBar';
import Notes, { NotesParent } from '../../components/communicationBar/Notes';
import { WsActivityProps } from '../../features/activityLog/ActivityHelper';
import { ClosureAction, useClosureAuthz } from '../../hooks/useClosureAuthz';
import { useDialog } from '../../hooks/useDialog';
import { useConversationWidgetStore } from '../../utils/useConversationWidgetStore';

import ClosureDocuments from './components/ClosureDocuments';
import ClosureInvoices from './components/ClosureInvoices';
import ClosurePlacement from './components/ClosurePlacement';
import PlacementStatusUpdateForm from './components/PlacementStatusUpdateForm';
import ShowClosureCandidateDetails from './components/ShowClosureCandidateDetails';
import { IInvoiceList } from './helper/payload';
import { ClosureDetailsDTO } from './types';

const optionsData: WsSelectObj[] = [];
interface IScrollableTab {
  tabName: string;
}

const ClosureDetails = () => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const location: any = useLocation();
  const [closure, setClosure] = useState<ClosureDetailsDTO>();
  const [invoiceList, setInvoiceList] = useState<IInvoiceList[]>([]);
  const [tabPanelPage, setTabPanelPage] = useState<number>(0);
  const [placementStatusName, setPlacementStatusName] = useState<string>('');
  const [placementStatusReason, setPlacementStatusReason] = useState<string>('');
  const [getActivityData, setgetActivityData] = useState<ActivityLogDTO[]>([]);
  const [openDialog, closeDialog] = useDialog();
  const [flag, setFlag] = useState<boolean>(false);
  const [tabList, setTabList] = useState<IScrollableTab[]>([]);
  const openModal = useConversationWidgetStore((state) => state.setCandidate);
  const updateView = useConversationWidgetStore((state) => state.updateView);
  const updateFromPage = useConversationWidgetStore((state) => state.updateFromPage);
  const activityLists: WsActivityProps[] = [];
  const [placementType, setPlacementType] = useState<string>('PERMANENT');

  const showCandidateDetails = useClosureAuthz(
    ClosureAction.VIEW_CLOSURE_DETAILS_CANDIDATE_DETAILS
  );
  const showPlacements = useClosureAuthz(ClosureAction.VIEW_CLOSURE_DETAILS_PLACEMENTS);
  const showDocuments = useClosureAuthz(ClosureAction.VIEW_CLOSURE_DETAILS_DOCUMENTS);
  const showInvoices = useClosureAuthz(ClosureAction.VIEW_CLOSURE_DETAILS_INVOICES);
  const showAddInvoice = useClosureAuthz(ClosureAction.ADD_CLOSURE_DETAILS_ADD_INVOICE);
  const showEditClosure = useClosureAuthz(ClosureAction.EDIT_CLOSURE);

  const getTabIndex = (label: string): number => {
    return tabList.findIndex((e) => e.tabName === label);
  };

  const fetchAll = useCallback(
    async (type: string) => {
      const closureResponse =
        type === 'PERMANENT'
          ? await getClosureDetailsById(params.id as string)
          : await getContractPlacementById(params.id as string);
      setPlacementStatusReason(closureResponse.data.placementStatusReason as string);
      const res = await getPlacementStatusList();
      optionsData.length = 0;
      res.data.forEach((data) => {
        if (closureResponse.data.placementStatusId === data.id) {
          setPlacementStatusName(data.placementStatusName);
        }
        optionsData.push({ label: data.placementStatusName, value: data.id });
      });
    },
    [params.id]
  );

  useEffect(() => {
    if (location.state?.placementType) {
      setPlacementType(location.state.placementType);
      fetchAll(location.state.placementType);
    } else {
      setPlacementType('PERMANENT');
      fetchAll('PERMANENT');
    }
  }, [fetchAll, flag, location]);

  useEffect(() => {
    let avtivityLog: any = [];

    const invoiceIds: IInvoiceList[] = [];
    (async () => {
      const response =
        placementType === 'PERMANENT'
          ? await getClosureById(params.id as string)
          : await getContractPlacementById(params.id as string);
      setClosure(new ClosureDetailsDTO(response.data));
      const invoiceResponse = await getClosureByCandidiateId(response.data.candidate.id);
      invoiceResponse.data.forEach((value) => invoiceIds.push({ id: value.clientInvoiceId }));
      setInvoiceList(invoiceIds);
      const { data: actvityResponseContext } = await getActivityLogs(params.id ?? '');
      const { data: actvityResponseEnitiy } = await getActivityLogsByEntity(params.id ?? '');
      avtivityLog = actvityResponseContext.concat(actvityResponseEnitiy);
      const uniqueAvtivityLog = avtivityLog.filter((obj, index) => {
        return index === avtivityLog.findIndex((o) => obj.id === o.id);
      });
      setgetActivityData(uniqueAvtivityLog);
    })();

    const setTabListByPermission = () => {
      const allowedTabList: IScrollableTab[] = [];
      if (showCandidateDetails) allowedTabList.push({ tabName: 'Candidate Details' });
      if (showDocuments) allowedTabList.push({ tabName: 'Documents' });
      if (showInvoices) allowedTabList.push({ tabName: 'Invoices' });
      if (showPlacements) allowedTabList.push({ tabName: 'Placements' });
      setTabList(allowedTabList);
    };

    setTabListByPermission();
  }, [params.id, showCandidateDetails, showDocuments, showInvoices, showPlacements, placementType]);

  const onSave = () => {
    closeDialog();
    setFlag(true);
    fetchAll(placementType);
  };

  const openPlacementStatusFormDialog = (defaultValues?) => {
    openDialog({
      children: (
        <PlacementStatusUpdateForm
          onSave={onSave}
          onCancel={closeDialog}
          options={optionsData}
          placementID={closure?.id}
          {...(defaultValues && { defaultValues })}
        />
      ),
    });
  };

  getActivityData?.map((data: ActivityLogDTO) =>
    activityLists.push({
      activity: (
        <TimelineItem>
          <Grid container>
            <Grid item xs={12}>
              <WsTypography color={WsTypoColors.TERTIARY}>
                {` At ${formatDateInDayMonthYearTime(data?.createdOn!.toString())} by ${
                  data.createdByName
                }`}
              </WsTypography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant={WsTypoVariants.SUB_TITLE}>
                <>{`${data.field} Category -> ${data.description} ${data.parentEntitiyName}`}</>
              </Typography>
            </Grid>
          </Grid>
        </TimelineItem>
      ),
      leftChildren: <CircleIcon sx={{ color: WsColors.TheBlues600 }} fontSize={'small'} />,
    })
  );

  if (!closure) return null;
  return (
    <Grid container sx={{ display: 'flex' }} mt={3}>
      <Grid item xs={12}>
        <Box bgcolor={WsColors.TheBlues900} borderRadius={1.5} p={4}>
          <Stack direction="row" spacing={2}>
            <ArrowBack
              cursor="pointer"
              sx={{ color: WsColors.White }}
              onClick={() => navigate('/placements')}
            />
            <Box>
              <Avatar sx={{ marginLeft: 2, backgroundColor: WsColors.TheBlues500 }}>
                {closure.candidate?.name?.charAt(0)?.toUpperCase() ?? ''}
              </Avatar>
            </Box>
            <Stack alignItems="flex-start">
              <WsTypography color={WsTypoColors.WHITE} variant={WsTypoVariants.TITLE} bold>
                {closure.candidate.name}
              </WsTypography>
              {closure.submissionId && (
                <Button
                  startIcon={<FaFileAlt />}
                  variant="text"
                  sx={{ paddingX: 0.5 }}
                  onClick={async () => {
                    updateView('submission');
                    updateFromPage('closure');
                    const { data: jobCandidateInteraction } = await getJobCandidateInteraction(
                      closure.submissionId
                    );
                    openModal(jobCandidateInteraction);
                  }}
                >
                  View CV
                </Button>
              )}
            </Stack>
            <WsChip
              value={isEmpty(placementStatusName) ? 'Placed' : placementStatusName}
              variant={WsChipVariants.PRIMARY}
            />
            {showEditClosure && (
              <Button
                style={{ marginLeft: 'auto' }}
                variant="contained"
                onClick={() => openPlacementStatusFormDialog()}
              >
                Update Status
              </Button>
            )}
            {showAddInvoice && (
              <WsButton
                variant="contained"
                onClick={() =>
                  navigate(
                    {
                      pathname: '/invoice/add',
                      search: createSearchParams({
                        placementType: placementType === 'PERMANENT' ? 'PERMANENT' : 'CONTRACTOR',
                      }).toString(),
                    },
                    {
                      state: {
                        ...(placementType === 'PERMANENT'
                          ? {
                              selectedCandidates: [
                                {
                                  data: {
                                    id: params.id,
                                    name: '',
                                    ouEntity: `${closure?.ourEntity?.id}`,
                                    clientEntity: `${closure?.client?.id}`,
                                    candidateName: `${closure?.candidate?.name}`,
                                    candidateEmail: `${closure?.candidate?.email}`,
                                    compensationAmount: `${closure?.sellRateAmount}`,
                                    feeArrangementAmount: `${closure?.feeArrangement.fee}`,
                                    feeArrangement: closure?.feeArrangement,
                                    currencySymbol: `${closure?.sellRateCurrency?.symbol}`,
                                    currencyId: `${closure?.sellRateCurrency?.id}`,
                                    commissionValue: closure.commissionValue,
                                    positionId: closure.positionId,
                                  },
                                },
                              ],
                            }
                          : { closure }),
                      },
                    }
                  )
                }
              >
                Create invoice
              </WsButton>
            )}
          </Stack>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <ScrollableTabs
          variant={TabTextVariant.SUB_TITLE}
          scrollList={tabList}
          getTabIndex={(index: number) => {
            setTabPanelPage(index);
          }}
          defaultValue={tabPanelPage}
        />
        {tabPanelPage == getTabIndex('Candidate Details') && params.id && (
          <ShowClosureCandidateDetails
            closure={closure}
            showEditClosure={showEditClosure ?? true}
            placementStatusReasonValue={placementStatusReason === null ? '' : placementStatusReason}
            placementType={placementType}
          />
        )}
        {tabPanelPage == getTabIndex('Documents') && params.id && showDocuments && (
          <ClosureDocuments closureId={params.id} showEditClosure={showEditClosure ?? true} />
        )}
        {tabPanelPage == getTabIndex('Invoices') && params.id && (
          <ClosureInvoices invoiceList={invoiceList} />
        )}
        {tabPanelPage == getTabIndex('Placements') && params.id && <ClosurePlacement />}
      </Grid>
      <CommunicationBar>
        <CommunicationBar.Item title="Activity log!" icon={BiHistory}>
          <Box m={2}>
            {getActivityData && (
              <Timeline>
                <Box ml="-100%">
                  <WsTimeLine activityList={activityLists} timeLineDotVariant={'outlined'} />
                </Box>
              </Timeline>
            )}
          </Box>
        </CommunicationBar.Item>
        <CommunicationBar.Item title="Notes" icon={BiDetail}>
          <Notes parent={NotesParent.PLACEMENT} parentId={closure.id} />
        </CommunicationBar.Item>
        <CommunicationBar.Item title="Coming soon!" icon={BiTimeFive}>
          <></>
        </CommunicationBar.Item>
        <CommunicationBar.Item title="Coming soon!" icon={BiMessageDetail}>
          <></>
        </CommunicationBar.Item>
        <CommunicationBar.Item title="Coming soon!" icon={BiEnvelope}>
          <></>
        </CommunicationBar.Item>
        <CommunicationBar.Item title="Coming soon!" icon={BiPhone}>
          <></>
        </CommunicationBar.Item>
      </CommunicationBar>
    </Grid>
  );
};

export default ClosureDetails;
